import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked, viewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { PAGE } from '../../app.routes';
import { APP_CONFIGURATION_TOKEN, LoaderService, TreatmentPlanSearch, TreatmentStatusType } from '../../core';
import { AnatomicalSideLabelPipe, BaseComponent, BoneTypeLabelPipe, ConvertDatePipe, PlanStatusLabelPipe, SortableHeaderComponent, SortEvent } from '../../shared';


/**
 * Component for table of plans.
 */
@Component({
	selector: 'tsp-plan-table',
	imports: [
		ConvertDatePipe,
		BoneTypeLabelPipe,
		PlanStatusLabelPipe,
		AnatomicalSideLabelPipe,
		SortableHeaderComponent
	],
	templateUrl: './plan-table.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanTableComponent extends BaseComponent {

	private readonly router = inject(Router);
	private readonly loaderSrv = inject(LoaderService);
	private readonly configs = inject(APP_CONFIGURATION_TOKEN);

	plans = input<TreatmentPlanSearch[]>();
	isFiltersCollapsed = input(false);

	sortedList = signal<TreatmentPlanSearch[]>([]);
	readonly fields = PlanFields;
	private readonly _DEFAULT_SORT: SortEvent = { column: this.fields.statusDate, direction: 'desc' };
	sortOrder = signal<SortEvent>({ ...this._DEFAULT_SORT });
	activeColumn = computed(() => this.sortOrder().column);

	readonly status = TreatmentStatusType;

	private readonly _headers = viewChildren(SortableHeaderComponent);


	private readonly initSortOrder = effect(() => {
		if (this.plans()) this.sortOrder.set({ ...this._DEFAULT_SORT });
	});

	private readonly sortList = effect(() => this._sortList());

	/**
	* Update column sort order and sort list.
	*/
	onSort(order: SortEvent) {
		const newOrder = (order.direction !== '' || order.column === this._DEFAULT_SORT.column) ? order : { ...this._DEFAULT_SORT };
		this.sortOrder.set(newOrder);
	}

	/**
	 * Navigate to plan settings edit page.
	 */
	planSettings(planGuid: string): void {
		this.router.navigate([PAGE.PLAN_SETTINGS], { queryParams: { planGuid: planGuid } });
	}

	/**
	 * Navigate to tlhex page.
	 */
	viewPlan(planGuid: string, planStatus: TreatmentStatusType): void {
		this.loaderSrv.show();
		if (planStatus === TreatmentStatusType.REVOKED) {
			window.location.href = this.configs.patListSite;
		} else {
			window.location.href = `${this.configs.tlhexSite}${planGuid}`;
		}
	}

	private _sortList(): void {
		this._headers().forEach(h => h.reset());
		this._headers().find(h => h.name() === this.sortOrder().column)?.direction.set(this.sortOrder().direction);
		const field = this.sortOrder().column as keyof TreatmentPlanSearch;
		const desc = this.sortOrder().direction === 'desc';
		untracked(() => this.sortedList.set([...this.plans()!].sort((a, b) => this._compareFunction(a[field], b[field], desc))));
	}

	private _compareFunction(a: any, b: any, desc: boolean): number {
		if (a > b) return desc ? -1 : 1;
		if (a < b) return desc ? 1 : -1;
		return 0;
	}

}


/**
* Fields for plan sorting.
*/
export enum PlanFields {
	planId = 'caseNumber',
	frameId = 'frameID',
	boneType = 'boneType',
	side = 'side',
	planStatus = 'status',
	statusDate = 'stateModificationDate'
}